import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyService } from './currency.service';
import { DataService } from './data.service';
import { StorageService } from './storage.service';
import { StoredItem } from '../shared/enums';

export interface LanguageOption {
  key: string;
  label: string;
}

@Injectable({
  providedIn: 'root'
})
export class SettingService {
  private translateService = inject(TranslateService);
  private currencyService = inject(CurrencyService);
  private dataService = inject(DataService);
  private storageService = inject(StorageService);

  hasMultiLanguage = signal<boolean>(false);
  hasMultiCurrency = signal<boolean>(false);

  readonly hasSettings = computed(() => this.hasMultiLanguage() || this.hasMultiCurrency());
  readonly venueLanguages = signal<string[]>([]);

  readonly languages = computed<LanguageOption[]>(() =>
    this.venueLanguages().map(key => ({ key, label: `LANGUAGES.${key.toUpperCase()}` }))
  );

  currencies = signal<string[]>([]);
  currentLanguage = signal<string>('');
  // Derived, not copied: always the currency prices are actually shown in - including a choice
  // remembered from an earlier visit, which only applies once its exchange rate has loaded.
  readonly currentCurrency = computed(() => this.currencyService.currentCurrency);

  constructor() {
    this.currencyService.currencies$.subscribe(currencies => this.currencies.set(currencies));

    // Prefetch as soon as we know the restaurant has multiple currencies, so the
    // bottom sheet already has data by the time the user taps the settings icon.
    effect(() => {
      if (this.hasMultiCurrency() && this.dataService.restaurant()) {
        this.setCurrencies();
      }
    });
  }

  setCurrencies(): void {
    this.currentLanguage.set(this.translateService.currentLang);
    this.currencyService.setCurrencies();
  }

  applyLanguage(lang: string): void {
    this.translateService.use(lang);
    this.storageService.set(StoredItem.Language, lang);
    this.currentLanguage.set(lang);
  }

  applyCurrency(currency: string): void {
    this.currencyService.setCurrentCurrency(currency);
  }
}
